import React, { useCallback, useEffect, useState } from 'react';
import { Gauge, Check, Clock } from 'lucide-react';
import Skeleton from '../common/Skeleton';
import NoDataFound from '../common/NoDataFound';
import Toast, { ToastMessage } from '../common/Toast';
import Button from '../common/Button';
import { usePermission } from '../../contexts/PermissionContext';
import extractApiError from '../../utils/apiError';
import pfmService, { PfmSubmission } from '../../services/pfmService';
import { RBAC_SUBMISSIONS } from './rbacPfm';

interface Props {
  /** 'archive' renders read-only history; 'open' is the live Action Centre panel. */
  scope?: 'open' | 'archive';
  readOnly?: boolean;
}

/**
 * The staff feeding form — one item in the Action Centre.
 *
 * Deliberately minimal: one task, done monthly, by people who are not GRC
 * specialists. It shows only the caller's own slots (row-scoped server-side by
 * PFM-RBAC-1), the previous period's value for context, and a single numeric
 * field. No catalogue browsing, no other users' data.
 */
const PfmMySubmissions: React.FC<Props> = ({ scope = 'open', readOnly = false }) => {
  const viewPerm = usePermission(RBAC_SUBMISSIONS, 'view');
  const modifyPerm = usePermission(RBAC_SUBMISSIONS, 'view+modify');
  const canView = viewPerm.allowed;
  const canModify = modifyPerm.allowed && !readOnly && scope === 'open';
  const permsLoading = viewPerm.loading;

  const [rows, setRows] = useState<PfmSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const load = useCallback(async () => {
    if (!canView) return;
    try {
      setLoading(true);
      setRows(await pfmService.getMySubmissions(scope === 'archive'));
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Failed to load your performance forms') });
    } finally {
      setLoading(false);
    }
  }, [canView, scope]);

  useEffect(() => { load(); }, [load]);

  const submit = async (row: PfmSubmission) => {
    const raw = (draft[row.id] ?? '').trim();
    if (raw === '') {
      setToast({ type: 'error', message: 'Enter a value before submitting.' });
      return;
    }
    // Signed decimals are legitimate here (a variance can be negative) — this
    // is exactly what validation code 209 exists for. The backend validates it.
    if (!/^-?\d+(\.\d+)?$/.test(raw)) {
      setToast({ type: 'error', message: 'Enter a number (decimals and negatives are allowed).' });
      return;
    }
    try {
      setSavingId(row.id);
      await pfmService.submitValue(row.id, Number(raw));
      setToast({ type: 'success', message: `${row.measure_name} submitted` });
      setDraft(d => { const n = { ...d }; delete n[row.id]; return n; });
      load();
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Failed to submit') });
    } finally {
      setSavingId(null);
    }
  };

  const isOverdue = (row: PfmSubmission) =>
    row.status === 'pending' && new Date(row.period_end) < new Date();

  if (!permsLoading && !canView) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center' }}>
        <NoDataFound title="Not authorised"
          message={viewPerm.message || 'You are not authorised to submit performance data.'} />
      </div>
    );
  }

  if (loading) {
    return <div style={{ padding: 12 }}><Skeleton.Box /><Skeleton.Box /><Skeleton.Box /></div>;
  }

  if (rows.length === 0) {
    return (
      <NoDataFound
        title={scope === 'archive' ? 'No past submissions' : 'Nothing to submit'}
        message={scope === 'archive'
          ? 'Your submitted performance figures will appear here.'
          : 'You have no performance measures awaiting entry this period.'}
      />
    );
  }

  // Overdue first — the whole point of the Action Centre.
  const ordered = [...rows].sort((a, b) => Number(isOverdue(b)) - Number(isOverdue(a)));

  return (
    <>
      <div className="table-list" data-testid="pfm-my-submissions">
        <div className="table-list-scroll">
          <table className="table-list-table">
            <thead>
              <tr>
                <th className="table-list-th">Measure</th>
                <th className="table-list-th">Where</th>
                <th className="table-list-th" style={{ width: 150 }}>Period</th>
                <th className="table-list-th" style={{ width: 110, textAlign: 'right' }}>Previous</th>
                <th className="table-list-th" style={{ width: 160 }}>Value</th>
                <th className="table-list-th" style={{ width: 70 }}>Unit</th>
                <th className="table-list-th" style={{ width: 120 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map(row => (
                <tr key={row.id} data-testid={`pfm-submission-row-${row.id}`}>
                  <td className="table-list-cell">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Gauge size={14} strokeWidth={1.75} style={{ opacity: .6 }} />
                      {row.measure_name}
                    </div>
                  </td>
                  <td className="table-list-cell">
                    {row.primary_node_label || '—'}
                    {row.secondary_node_label && (
                      <span style={{ opacity: .6 }}> × {row.secondary_node_label}</span>
                    )}
                  </td>
                  <td className="table-list-cell">
                    {row.period_start}
                    {isOverdue(row) && (
                      <span className="status-badge danger" style={{ marginLeft: 6 }}>overdue</span>
                    )}
                  </td>
                  <td className="table-list-cell" style={{ textAlign: 'right', opacity: .65, fontVariantNumeric: 'tabular-nums' }}>
                    {row.previous_value == null
                      ? '—'
                      : Number(row.previous_value).toFixed(row.decimal_places ?? 2)}
                  </td>
                  <td className="table-list-cell">
                    {row.status === 'pending' && canModify ? (
                      <input
                        type="text" inputMode="decimal"
                        value={draft[row.id] ?? ''}
                        placeholder="enter value"
                        data-testid={`pfm-submission-input-${row.id}`}
                        onChange={(e) => setDraft(d => ({ ...d, [row.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') submit(row); }}
                        style={{ width: '100%' }}
                      />
                    ) : (
                      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {row.value == null ? '—' : Number(row.value).toFixed(row.decimal_places ?? 2)}
                      </span>
                    )}
                  </td>
                  <td className="table-list-cell">{row.unit || '—'}</td>
                  <td className="table-list-cell">
                    {row.status === 'pending' && canModify ? (
                      <Button variant="fill" onClick={() => submit(row)}
                              disabled={savingId === row.id}
                              data-testid={`pfm-submission-save-${row.id}`}>
                        {savingId === row.id ? 'Saving…' : 'Submit'}
                      </Button>
                    ) : row.status === 'pending' ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 5, opacity: .65 }}>
                        <Clock size={13} /> pending
                      </span>
                    ) : row.status === 'submitted' ? (
                      <span className="status-badge warning">awaiting review</span>
                    ) : row.status === 'rejected' ? (
                      <span className="status-badge danger">rejected</span>
                    ) : (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--green-text, #1a6e46)' }}>
                        <Check size={13} strokeWidth={2.2} /> recorded
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
};

export default PfmMySubmissions;
